import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { z } from 'zod'

import { FormErrorSummary } from '@/components/form/FormErrorSummary'
import { FormInput } from '@/components/form/FormInput'
import { Button } from '@/components/ui/Button'
import { useResetPassword } from '@/features/auth/hooks/useAuth'
import { otpCodeSchema, passwordSchema } from '@/features/auth/schemas'
import { AuthLayout } from '@/layouts/AuthLayout'
import { getErrorMessage, getFieldErrors } from '@/utils/errors'

const schema = z
  .object({
    email: z.email('Enter a valid email address'),
    code: otpCodeSchema,
    password: passwordSchema,
    password_confirmation: z.string(),
  })
  .refine((data) => data.password === data.password_confirmation, {
    message: 'Passwords do not match',
    path: ['password_confirmation'],
  })

type FormValues = z.infer<typeof schema>

export function ResetPasswordPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const prefillEmail = (location.state as { email?: string } | null)?.email ?? ''
  const resetPassword = useResetPassword()
  const [apiError, setApiError] = useState<unknown>(null)

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: prefillEmail, code: '', password: '', password_confirmation: '' },
  })

  const onSubmit = handleSubmit((values) => {
    setApiError(null)
    resetPassword.mutate(values, {
      onSuccess: () => void navigate('/login', { replace: true }),
      onError: (error) => setApiError(error),
    })
  })

  return (
    <AuthLayout title="Reset Password" subtitle="Enter the code we emailed you and a new password">
      <FormErrorSummary
        errors={errors}
        serverErrors={getFieldErrors(apiError)}
        generalMessage={apiError ? getErrorMessage(apiError) : null}
      />
      <form onSubmit={(e) => void onSubmit(e)} noValidate>
        <FormInput control={control} name="email" label="Email" type="email" required />
        <FormInput control={control} name="code" label="Reset Code" required />
        <FormInput
          control={control}
          name="password"
          label="New Password"
          type="password"
          required
        />
        <FormInput
          control={control}
          name="password_confirmation"
          label="Confirm New Password"
          type="password"
          required
        />
        <Button type="submit" className="w-100" loading={resetPassword.isPending}>
          Reset Password
        </Button>
      </form>
      <div className="text-center mt-3">
        <p className="text-muted mb-0">
          <Link to="/login" className="text-primary">
            Back to Sign In
          </Link>
        </p>
      </div>
    </AuthLayout>
  )
}
